import { useEffect, useState } from 'react';

/**
 * Mantiene un componente montado durante su animación de salida.
 *
 * Patrón típico de un sheet / modal / dropdown con animación de entrada:
 *
 * ```
 * const [animated, setAnimated] = useState(false);
 * useEffect(() => {
 *   if (open) requestAnimationFrame(() => setAnimated(true));
 *   else setAnimated(false);
 * }, [open]);
 * if (!open) return null;
 * ```
 *
 * El problema con ese patrón: cuando `open` pasa a `false` (porque el
 * user seleccionó algo y el padre lo cerró), el `return null` desmonta
 * el componente al instante. La CSS transition que apaga la animación
 * no llega a correr porque el DOM ya se fue.
 *
 * Este hook arregla eso:
 *   - Cuando `open` pasa a `true` → mounted=true, animated=true en el
 *     siguiente frame (igual que el patrón original).
 *   - Cuando `open` pasa a `false` → animated=false al toque (dispara la
 *     CSS exit transition), mounted sigue en true durante `duration` ms,
 *     y recién después mounted pasa a false (componente se desmonta).
 *
 * Uso:
 *
 * ```
 * const { mounted, animated } = useExitTransition(open, 300);
 * if (!mounted) return null;
 * return (
 *   <div className={`transition-transform duration-300 ${animated ? '' : 'translate-y-full'}`}>
 *     ...
 *   </div>
 * );
 * ```
 */
export function useExitTransition(
  open: boolean,
  duration: number = 300,
): { mounted: boolean; animated: boolean } {
  // Sí, mounted arranca en `open` para que cuando el padre arranque con
  // `open=true` el componente se mantenga renderizado desde el primer
  // tick. Si arranca con `open=false`, no se renderiza nada.
  const [mounted, setMounted] = useState<boolean>(open);
  const [animated, setAnimated] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Doble rAF: el primer rAF garantiza que el navegador ya
      // commiteó el mount con `animated=false`; el segundo dispara el
      // cambio a `true` para que CSS detecte la transición. Sin doble
      // rAF en algunos browsers React batchea ambas updates en el mismo
      // commit y la animación de entrada se "saltea".
      let id1: number;
      let id2: number;
      id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setAnimated(true));
      });
      return () => {
        cancelAnimationFrame(id1);
        cancelAnimationFrame(id2);
      };
    }
    // open === false → empieza la salida. Animated pasa a false ahora,
    // y dejamos el componente montado durante `duration` ms para que la
    // CSS transition tenga tiempo de correr antes del unmount.
    setAnimated(false);
    if (!mounted) return;
    const t = window.setTimeout(() => setMounted(false), duration);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, duration]);

  return { mounted, animated };
}
